// Pure operations on a year file. Each returns a new year file; nothing is mutated.
// Ids and timestamps come from the caller: tax-core never reads a clock or a random source.
import type { ArchivedRecord, Collection, Id, Person, PersonIncome, Screening, Source, YearFile } from "./types.ts";
import { UNKNOWN } from "./values.ts";

export function emptyYearFile(taxYear: number): YearFile {
  return {
    schema: 1,
    taxYear,
    household: { filingStatus: null, allFuturesAccountsListed: null, allPropFirmsListed: null },
    people: [],
    screening: {},
    income: {},
    firms: [],
    accounts: [],
    sources: [],
    events: [],
    links: [],
    corrections: [],
    decisions: [],
    history: [],
  };
}

export const UNANSWERED_SCREENING: Screening = {
  nonresident: null,
  entity: null,
  section475: null,
  straddlesOrHedges: null,
  nonUsd: null,
  ownAccountTraderDeductions: null,
};

export const UNKNOWN_INCOME: PersonIncome = { socialSecurityWages: UNKNOWN, otherSeEarnings: UNKNOWN };

export function addPerson(yf: YearFile, person: Person): YearFile {
  if (yf.people.some((p) => p.id === person.id)) throw new Error(`Person ${person.id} already exists`);
  if (person.role === "primary" && yf.people.some((p) => p.role === "primary")) throw new Error("There is already a primary taxpayer");
  if (person.role === "spouse" && yf.people.some((p) => p.role === "spouse")) throw new Error("There is already a spouse");
  return {
    ...yf,
    people: [...yf.people, person],
    screening: { ...yf.screening, [person.id]: { ...UNANSWERED_SCREENING } },
    income: { ...yf.income, [person.id]: { ...UNKNOWN_INCOME } },
  };
}

type RecordOf<C extends Collection> = Extract<ArchivedRecord, { collection: C }>["record"];
type NewRecord<C extends Collection> = RecordOf<C> extends infer R ? (R extends unknown ? Omit<R, "revision" | "deletedAt"> : never) : never;

/** Add a record at revision 1. Ids must be unique within their collection. */
export function addRecord<C extends Collection>(yf: YearFile, collection: C, record: NewRecord<C>): YearFile {
  const list = yf[collection] as RecordOf<C>[];
  const id = (record as unknown as { id: Id }).id;
  if (list.some((r) => r.id === id)) throw new Error(`${collection} ${id} already exists`);
  const full = { ...record, revision: 1, deletedAt: null } as unknown as RecordOf<C>;
  return { ...yf, [collection]: [...list, full] };
}

export type AddSourceResult = { yearFile: YearFile; id: Id; existed: boolean };

/** Add a source once. A second import of the same document or row returns the existing one. */
export function addSource(yf: YearFile, source: NewRecord<"sources">): AddSourceResult {
  const existing = yf.sources.find((s) => s.fingerprint === source.fingerprint && s.deletedAt === null);
  if (existing) return { yearFile: yf, id: existing.id, existed: true };
  return { yearFile: addRecord(yf, "sources", source), id: source.id, existed: false };
}

/** Replace a record with a new revision, keeping the old one in history. */
export function reviseRecord<C extends Collection>(yf: YearFile, collection: C, id: Id, changes: Partial<RecordOf<C>>): YearFile {
  const list = yf[collection] as RecordOf<C>[];
  const current = list.find((r) => r.id === id);
  if (!current) throw new Error(`${collection} ${id} not found`);
  if (current.deletedAt !== null) throw new Error(`${collection} ${id} was removed`);
  const next = { ...current, ...changes, id, revision: current.revision + 1, deletedAt: null } as unknown as RecordOf<C>;
  if (collection === "sources") assertSameSourceKind(current as Source, next as Source);
  return {
    ...yf,
    [collection]: list.map((r) => (r.id === id ? next : r)),
    history: [...yf.history, { collection, record: current } as ArchivedRecord],
  };
}

/** Remove a record. It stays in the file, marked removed, so earlier reports can still be explained. */
export function removeRecord(yf: YearFile, collection: Collection, id: Id, at: string): YearFile {
  const list = yf[collection] as { id: Id; revision: number; deletedAt: string | null }[];
  const current = list.find((r) => r.id === id);
  if (!current) throw new Error(`${collection} ${id} not found`);
  if (current.deletedAt !== null) return yf;
  return {
    ...yf,
    [collection]: list.map((r) => (r.id === id ? { ...r, revision: r.revision + 1, deletedAt: at } : r)),
    history: [...yf.history, { collection, record: current } as ArchivedRecord],
  };
}

function assertSameSourceKind(a: Source, b: Source) {
  if (a.kind !== b.kind) throw new Error("A source can't change kind; remove it and add a new one");
}

export function live<T extends { deletedAt: string | null }>(list: T[]): T[] {
  return list.filter((r) => r.deletedAt === null);
}
